import { useCallback, useEffect, useRef, useState } from "react";

/** Kept in step with the exit animation in DataKitDrawer.scss. */
export const DRAWER_EXIT_MS = 180;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Holds a drawer on screen long enough to play its exit animation.
 *
 * Every Data Kit drawer is rendered conditionally by its parent (`{editing && <Drawer/>}`), so
 * calling `onClose` unmounts it on the spot and no CSS transition ever gets a frame to run. This
 * delays whatever the caller wanted to do and hands back a `closing` flag for the class that plays
 * the animation, which keeps the whole concern inside the drawer — the parents stay untouched.
 *
 * Guarded by a ref rather than the state value: a second click while the first exit is already
 * running would otherwise queue a second timer and fire the callback twice.
 *
 * Under `prefers-reduced-motion` it finishes immediately, matching the stylesheet, which disables
 * the animation there — a delay with nothing to watch is just lag.
 */
export function useDrawerDismiss() {
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const dismiss = useCallback((finish: () => void) => {
    if (closingRef.current) return;
    closingRef.current = true;

    if (prefersReducedMotion()) {
      finish();
      return;
    }
    setClosing(true);
    timer.current = window.setTimeout(finish, DRAWER_EXIT_MS);
  }, []);

  return { closing, dismiss };
}
